import type { Player, Match, Round } from "./types";
import { getStandings, getPreviousOpponents, hadBye } from "./data";

let matchIdCounter = 0;
const nextMatchId = (): string => `m${++matchIdCounter}`;

function makeBye(playerID: number, winThreshold: number): Match {
  return {
    id: nextMatchId(),
    player1: playerID,
    player2: null,
    player1Games: winThreshold,
    player2Games: 0,
    isBye: true,
  };
}

function makeMatch(player1ID: number, player2ID: number): Match {
  return {
    id: nextMatchId(),
    player1: player1ID,
    player2: player2ID,
    player1Games: null,
    player2Games: null,
    isBye: false,
  };
}

/**
 * Round 1 uses fold pairing: split by seed, match across halves.
 * 1v5, 2v6, 3v7, 4v8 for 8 players. Odd count gives the last
 * player a bye.
 */
export function generateRound1(players: Player[], bestOf: 3 | 5): Round {
  const sorted = [...players].sort((a, b) => a.seed - b.seed);
  const half = Math.ceil(sorted.length / 2);
  const top = sorted.slice(0, half);
  const bottom = sorted.slice(half);
  const winThreshold = Math.ceil(bestOf / 2);

  const matches: Match[] = [];
  for (let i = 0; i < top.length; i++) {
    if (i < bottom.length) {
      matches.push(makeMatch(top[i]!.id, bottom[i]!.id));
    } else {
      matches.push(makeBye(top[i]!.id, winThreshold));
    }
  }

  return { roundNum: 1, matches };
}

/**
 * Rounds 2+ group players by record (best to worst), then pair
 * top vs bottom within each group. Unpaired remainders float
 * down into the next group. Rematches are avoided when possible.
 */
export function generateNextRound(
  players: Player[],
  rounds: Round[],
  bestOf: number,
): Round {
  const standings = getStandings(rounds, players);
  const active = standings;
  const winThreshold = Math.ceil(bestOf / 2);

  // Group by record string, e.g. "2-1"
  const groups = new Map<string, typeof active>();
  for (const p of active) {
    const key = `${p.wins}-${p.losses}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(p);
  }

  // Sort group keys best-to-worst by net record
  const sortedKeys = [...groups.keys()].sort((a, b) => {
    const aParts = a.split("-").map(Number);
    const bParts = b.split("-").map(Number);
    return bParts[0]! - bParts[1]! - (aParts[0]! - aParts[1]!);
  });

  const matches: Match[] = [];
  const paired = new Set<number>();
  let floater: (typeof active)[number] | null = null;

  for (const key of sortedKeys) {
    let pool = groups.get(key)!.filter((p) => !paired.has(p.id));

    // Absorb floater from the higher group
    if (floater) {
      pool = [floater, ...pool];
      floater = null;
    }

    // Pair top vs bottom within pool, avoiding rematches
    const unpaired = [...pool];
    while (unpaired.length >= 2) {
      const p1 = unpaired.shift()!;
      const prevOpps = getPreviousOpponents(rounds, p1.id);

      // Search from bottom for someone p1 hasn't played
      let foundIdx = -1;
      for (let i = unpaired.length - 1; i >= 0; i--) {
        if (!prevOpps.has(unpaired[i]!.id)) {
          foundIdx = i;
          break;
        }
      }

      // All options are rematches — take the bottom anyway
      if (foundIdx === -1) foundIdx = unpaired.length - 1;

      const p2 = unpaired.splice(foundIdx, 1)[0]!;
      matches.push(makeMatch(p1.id, p2.id));
      paired.add(p1.id);
      paired.add(p2.id);
    }

    if (unpaired.length === 1) {
      floater = unpaired[0]!;
    }
  }

  // Leftover floater gets a bye. If they've already had one, try to swap them
  // into the most recent match with a player who hasn't had a bye yet, so
  // that player takes the bye instead.
  if (floater) {
    let assigned = false;

    if (hadBye(rounds, floater.id)) {
      for (let i = matches.length - 1; i >= 0; i--) {
        const match = matches[i]!;
        if (match.isBye) continue;

        const swapWithP1 = !hadBye(rounds, match.player1);
        const swapWithP2 = !hadBye(rounds, match.player2!);
        if (!swapWithP1 && !swapWithP2) continue;

        if (swapWithP1) {
          matches.push(makeBye(match.player1, winThreshold));
          matches[i] = makeMatch(floater.id, match.player2!);
        } else {
          matches.push(makeBye(match.player2!, winThreshold));
          matches[i] = makeMatch(match.player1, floater.id);
        }
        assigned = true;
        break;
      }
    }

    if (!assigned) {
      matches.push(makeBye(floater.id, winThreshold));
    }
  }

  return { roundNum: rounds.length + 1, matches };
}
